// Mantenedor genérico (docs/108 B10): una lista filtrable y un formulario declarativo para crear o
// editar. Cada maestro de Configuración se describe con columnas, campos y cómo arma su pedido; las
// reglas (unicidad, formatos, permisos) las impone el backend y su error se muestra tal cual.
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { useSesion } from '../sesion/SesionContexto';
import { PanelError } from './Estados';
import { Grilla, type Columna } from './Grilla';
import { Modal } from './Modal';

export interface CampoForm {
  id: string;
  etiqueta: string;
  tipo?: 'texto' | 'numero' | 'decimal' | 'seleccion' | 'booleano' | 'fecha' | 'area';
  opciones?: Array<{ valor: string; texto: string }>;
  requerido?: boolean;
  /** Solo al crear (identidad: código, RUT…). */
  soloCrear?: boolean;
  ayuda?: string;
  patron?: RegExp;
}

export interface PedidoMantenedor {
  ruta: string;
  cuerpo: unknown;
  metodo?: 'POST' | 'PATCH' | 'PUT';
}

export interface AccionFila<T> {
  texto: string;
  /** Pide un motivo antes (cambios sensibles). */
  conMotivo?: boolean;
  permiso?: string;
  visible?: (f: T) => boolean;
  /** Acción LOCAL (abrir un editor propio): no manda nada. */
  abrir?: (f: T) => void;
  pedido?: (f: T, motivo: string) => PedidoMantenedor;
  /** La respuesta de la acción (p. ej. el enlace de activación que se muestra una sola vez). */
  alHecho?: (respuesta: unknown, f: T) => void;
}

export interface DefMantenedor<T> {
  etiqueta: string;
  claveConsulta: QueryKey;
  listar: () => Promise<T[]>;
  clave: (f: T) => string;
  columnas: Columna<T>[];
  /** Texto en que busca el filtro rápido. */
  textoDe?: (f: T) => string;
  campos?: CampoForm[];
  valoresDe?: (f: T) => Record<string, string>;
  crear?: (v: Record<string, string>, motivo: string) => PedidoMantenedor;
  editar?: (f: T, v: Record<string, string>, motivo: string) => PedidoMantenedor;
  permisoCrear?: string;
  permisoEditar?: string;
  /** El comando exige motivo (permiso sensible): el formulario lo pide. */
  conMotivo?: boolean;
  acciones?: AccionFila<T>[];
  /** La respuesta del alta (p. ej. una clave que el backend muestra una sola vez). */
  alCreado?: (respuesta: unknown) => void;
  /** Algo bajo la grilla (detalle de la fila elegida). */
  detalle?: (f: T) => ReactNode;
  nota?: ReactNode;
}

function useGuardar(claveConsulta: QueryKey) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  return useMutation<unknown, Error, PedidoMantenedor>({
    mutationFn: ({ ruta, cuerpo, metodo = 'POST' }) => {
      const idempotencia = intentos.clave(`mant:${metodo}:${ruta}`, cuerpo);
      return metodo === 'PATCH'
        ? api.patch(ruta, cuerpo, { idempotencia })
        : metodo === 'PUT'
          ? api.put(ruta, cuerpo, { idempotencia })
          : api.post(ruta, cuerpo, { idempotencia });
    },
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: claveConsulta });
    },
    onError: (e) => intentos.cerrar(e),
  });
}

export function Mantenedor<T>({ def }: { def: DefMantenedor<T> }) {
  const { puede } = useSesion();
  const q = useQuery({ queryKey: def.claveConsulta, queryFn: def.listar });
  const guardar = useGuardar(def.claveConsulta);
  const [filtro, setFiltro] = useState('');
  const [form, setForm] = useState<{ fila: T | null } | null>(null);
  const [accion, setAccion] = useState<{ a: AccionFila<T>; f: T } | null>(null);
  const [sel, setSel] = useState<T | null>(null);
  const filas = (q.data ?? []).filter(
    (f) =>
      !filtro.trim() ||
      (def.textoDe?.(f) ?? '').toLowerCase().includes(filtro.trim().toLowerCase()),
  );
  const puedeCrear = !!def.crear && (!def.permisoCrear || puede(def.permisoCrear));
  const puedeEditar = !!def.editar && (!def.permisoEditar || puede(def.permisoEditar));
  const acciones = (def.acciones ?? []).filter((a) => !a.permiso || puede(a.permiso));
  const columnas: Columna<T>[] = [
    ...def.columnas,
    ...(puedeEditar || acciones.length
      ? [
          {
            id: '__acc',
            titulo: '',
            ancho: `${4 + acciones.length * 6}rem`,
            celda: (f: T) => (
              <span className="acciones-fila">
                {puedeEditar && (
                  <button type="button" className="boton-mini" onClick={() => setForm({ fila: f })}>
                    Editar
                  </button>
                )}
                {acciones
                  .filter((a) => !a.visible || a.visible(f))
                  .map((a) => (
                    <button
                      key={a.texto}
                      type="button"
                      className="boton-mini"
                      onClick={() =>
                        a.abrir
                          ? a.abrir(f)
                          : a.conMotivo
                            ? setAccion({ a, f })
                            : a.pedido &&
                              guardar.mutate(a.pedido(f, ''), {
                                onSuccess: (r) => a.alHecho?.(r, f),
                              })
                      }
                    >
                      {a.texto}
                    </button>
                  ))}
              </span>
            ),
          } satisfies Columna<T>,
        ]
      : []),
  ];
  return (
    <div className="pantalla consulta" data-pantalla="mantenedor">
      <div className="barra-filtros">
        {def.textoDe && (
          <label className="campo">
            <span className="etiqueta">Buscar</span>
            <input
              aria-label={`Buscar en ${def.etiqueta}`}
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            />
          </label>
        )}
        <span className="espaciador" />
        <span className="apagado">{filas.length} registros</span>
        {puedeCrear && (
          <button type="button" className="primario" onClick={() => setForm({ fila: null })}>
            Nuevo
          </button>
        )}
      </div>
      {def.nota}
      {q.isError && <PanelError error={q.error} />}
      {guardar.isError && !form && !accion && <PanelError error={guardar.error} />}
      <Grilla
        etiqueta={def.etiqueta}
        columnas={columnas}
        filas={filas}
        clave={def.clave}
        cargando={q.isLoading}
        vacio="Sin registros"
        alSeleccionar={setSel}
        {...(puedeEditar ? { alAbrir: (f: T) => setForm({ fila: f }) } : {})}
      />
      {sel && def.detalle?.(sel)}
      {form && def.campos && (
        <FormularioMantenedor
          def={def}
          fila={form.fila}
          pendiente={guardar.isPending}
          error={guardar.isError ? guardar.error : null}
          alGuardar={(v, motivo) =>
            guardar.mutate(form.fila ? def.editar!(form.fila, v, motivo) : def.crear!(v, motivo), {
              onSuccess: (r) => {
                if (!form.fila) def.alCreado?.(r);
                setForm(null);
              },
            })
          }
          alCerrar={() => {
            guardar.reset();
            setForm(null);
          }}
        />
      )}
      {accion && (
        <Modal titulo={accion.a.texto} alCerrar={() => setAccion(null)} ancho="angosto">
          <MotivoYConfirmar
            texto={accion.a.texto}
            pendiente={guardar.isPending}
            error={guardar.isError ? guardar.error : null}
            alConfirmar={(m) =>
              accion.a.pedido &&
              guardar.mutate(accion.a.pedido(accion.f, m), {
                onSuccess: (r) => {
                  setAccion(null);
                  accion.a.alHecho?.(r, accion.f);
                },
              })
            }
            alCerrar={() => setAccion(null)}
          />
        </Modal>
      )}
    </div>
  );
}

function MotivoYConfirmar({
  texto,
  pendiente,
  error,
  alConfirmar,
  alCerrar,
}: {
  texto: string;
  pendiente: boolean;
  error: unknown;
  alConfirmar: (m: string) => void;
  alCerrar: () => void;
}) {
  const [m, setM] = useState('');
  return (
    <>
      <label className="campo">
        <span className="etiqueta">Motivo</span>
        <input aria-label="Motivo" autoFocus value={m} onChange={(e) => setM(e.target.value)} />
      </label>
      {error ? <PanelError error={error} /> : null}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={m.trim().length < 3 || pendiente}
          onClick={() => alConfirmar(m.trim())}
        >
          {texto}
        </button>
      </div>
    </>
  );
}

function FormularioMantenedor<T>({
  def,
  fila,
  pendiente,
  error,
  alGuardar,
  alCerrar,
}: {
  def: DefMantenedor<T>;
  fila: T | null;
  pendiente: boolean;
  error: unknown;
  alGuardar: (v: Record<string, string>, motivo: string) => void;
  alCerrar: () => void;
}) {
  const [v, setV] = useState<Record<string, string>>(() =>
    fila && def.valoresDe ? def.valoresDe(fila) : {},
  );
  const [motivo, setMotivo] = useState('');
  const campos = (def.campos ?? []).filter((c) => !(fila && c.soloCrear));
  const problemas = campos.filter((c) => {
    const x = (v[c.id] ?? '').trim();
    if (c.requerido && !x && c.tipo !== 'booleano') return true;
    if (x && c.patron && !c.patron.test(x)) return true;
    if (x && c.tipo === 'numero' && !/^\d+$/.test(x)) return true;
    if (x && c.tipo === 'decimal' && !/^\d+([.,]\d+)?$/.test(x)) return true;
    return false;
  });
  const valido = problemas.length === 0 && (!def.conMotivo || motivo.trim().length >= 3);
  return (
    <Modal titulo={`${fila ? 'Editar' : 'Nuevo'} · ${def.etiqueta}`} alCerrar={alCerrar}>
      <form
        aria-label={`Formulario ${def.etiqueta}`}
        onSubmit={(e) => {
          e.preventDefault();
          if (valido) alGuardar(v, motivo.trim());
        }}
      >
        {campos.map((c) => (
          <label key={c.id} className="campo">
            <span className="etiqueta">
              {c.etiqueta}
              {c.requerido ? ' *' : ''}
            </span>
            {c.tipo === 'seleccion' ? (
              <select
                aria-label={c.etiqueta}
                value={v[c.id] ?? ''}
                onChange={(e) => setV((x) => ({ ...x, [c.id]: e.target.value }))}
              >
                <option value="">—</option>
                {(c.opciones ?? []).map((o) => (
                  <option key={o.valor} value={o.valor}>
                    {o.texto}
                  </option>
                ))}
              </select>
            ) : c.tipo === 'booleano' ? (
              <input
                type="checkbox"
                aria-label={c.etiqueta}
                checked={v[c.id] === 'true'}
                onChange={(e) =>
                  setV((x) => ({ ...x, [c.id]: e.target.checked ? 'true' : 'false' }))
                }
              />
            ) : c.tipo === 'area' ? (
              <textarea
                aria-label={c.etiqueta}
                rows={3}
                value={v[c.id] ?? ''}
                onChange={(e) => setV((x) => ({ ...x, [c.id]: e.target.value }))}
              />
            ) : (
              <input
                aria-label={c.etiqueta}
                type={c.tipo === 'fecha' ? 'date' : 'text'}
                inputMode={c.tipo === 'numero' || c.tipo === 'decimal' ? 'decimal' : undefined}
                value={v[c.id] ?? ''}
                onChange={(e) => setV((x) => ({ ...x, [c.id]: e.target.value }))}
              />
            )}
            {c.ayuda && <span className="apagado">{c.ayuda}</span>}
          </label>
        ))}
        {def.conMotivo && (
          <label className="campo">
            <span className="etiqueta">Motivo del cambio *</span>
            <input
              aria-label="Motivo del cambio"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
        )}
        {problemas.length > 0 && (
          <p className="apagado">Revisa: {problemas.map((p) => p.etiqueta).join(', ')}</p>
        )}
        {error ? <PanelError error={error} /> : null}
        <div className="acciones-modal">
          <button type="button" onClick={alCerrar}>
            Volver
          </button>
          <button type="submit" className="primario" disabled={!valido || pendiente}>
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Texto decimal digitado con coma → el que acepta la API (punto). */
export const decimalApi = (t: string | undefined) => {
  const x = (t ?? '').trim();
  return x.includes(',') ? x.replace(/\./g, '').replace(',', '.') : x;
};
export const siTexto = <K extends string>(k: K, t: string | undefined) =>
  t && t.trim() ? ({ [k]: t.trim() } as Record<K, string>) : ({} as Record<K, string>);
